import { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, NavLink } from 'react-router-dom';
import SplashScreen from './components/SplashScreen';
import AchievementToast from './components/AchievementToast';
import Home from './pages/Home';
import Session from './pages/Session';
import VocalRange from './pages/VocalRange';
import Warmup from './pages/Warmup';
import Achievements from './pages/Achievements';
import Song from './pages/Song';
import Songs from './pages/Songs';
import Settings from './pages/Settings';
import { useVeloraStore } from './store/useVeloraStore';
import { THEMES } from './lib/themes';
import Onboarding from './pages/Onboarding';

const NAV = [
  { to: '/', label: 'Home', icon: (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none">
      <path d="M3 12L12 3l9 9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M5 10v9a1 1 0 001 1h4v-5h4v5h4a1 1 0 001-1v-9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )},
  { to: '/session', label: 'Session', icon: (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="1.8"/>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.2" strokeDasharray="3 3"/>
    </svg>
  )},
  { to: '/range', label: 'Range', icon: (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none">
      <rect x="4" y="3" width="2.5" height="18" rx="1.25" stroke="currentColor" strokeWidth="1.6"/>
      <rect x="9" y="7" width="2.5" height="14" rx="1.25" stroke="currentColor" strokeWidth="1.6"/>
      <rect x="14" y="5" width="2.5" height="16" rx="1.25" stroke="currentColor" strokeWidth="1.6"/>
      <rect x="19" y="9" width="2.5" height="12" rx="1.25" stroke="currentColor" strokeWidth="1.6"/>
    </svg>
  )},
  { to: '/warmup', label: 'Warmup', icon: (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5"/>
      <polyline points="12,7 12,12 15,15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    </svg>
  )},
  { to: '/songs', label: 'Songs', icon: (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none">
      <path d="M9 18V5l12-2v13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="6" cy="18" r="3" stroke="currentColor" strokeWidth="1.8"/>
      <circle cx="18" cy="16" r="3" stroke="currentColor" strokeWidth="1.8"/>
    </svg>
  )},
  { to: '/achievements', label: 'Progress', icon: (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none">
      <polyline points="4,17 8,12 12,14 16,9 20,11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      <line x1="4" y1="21" x2="20" y2="21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  )},
];

function OrbBackground({ theme }: { theme: typeof THEMES.dark }) {
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
      <div className="orb-1 absolute w-96 h-96 rounded-full" style={{
        background: theme.orb1,
        top: '-80px', left: '-60px',
        filter: 'blur(72px)',
        opacity: 0.9,
      }} />
      <div className="orb-2 absolute w-80 h-80 rounded-full" style={{
        background: theme.orb2,
        bottom: '15%', right: '-40px',
        filter: 'blur(64px)',
        opacity: 0.85,
      }} />
      <div className="orb-3 absolute w-72 h-72 rounded-full" style={{
        background: theme.orb3,
        bottom: '-60px', left: '20%',
        filter: 'blur(80px)',
        opacity: 0.7,
      }} />
      <div className="orb-4 absolute w-full h-full" style={{
        background: theme.orb4,
        top: 0, left: 0,
        filter: 'blur(40px)',
        opacity: 0.6,
        borderRadius: 0,
      }} />
    </div>
  );
}

export default function App() {
  const [showSplash, setShowSplash] = useState(true);
  const storeTheme = useVeloraStore(s => s.theme);
  const onboarded = useVeloraStore(s => s.onboarded);
  const navTheme = THEMES[storeTheme];
  const installPromptRef = useRef<BeforeInstallPromptEvent | null>(null);
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    import('./lib/notify').then(({ requestNotifyPermission }) => requestNotifyPermission());
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      installPromptRef.current = e as BeforeInstallPromptEvent;
      setInstallPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  if (!onboarded) {
    return <Onboarding />;
  }

  if (showSplash) {
    return <SplashScreen onFinished={() => setShowSplash(false)} installPrompt={installPrompt} />;
  }

  return (
    <BrowserRouter>
      <div className="relative min-h-screen" style={{ background: navTheme.bg }}>
        {/* Animated orb background */}
        <OrbBackground theme={navTheme} />

        <AchievementToast />

        <div className="relative" style={{ zIndex: 1 }}>
          <Routes>
            <Route path="/"            element={<Home />} />
            <Route path="/session"     element={<Session />} />
            <Route path="/range"       element={<VocalRange />} />
            <Route path="/warmup"      element={<Warmup />} />
            <Route path="/songs"       element={<Songs />} />
            <Route path="/song"        element={<Song />} />
            <Route path="/achievements" element={<Achievements />} />
            <Route path="/settings"    element={<Settings />} />
          </Routes>
        </div>

        {/* Liquid glass bottom nav */}
        <nav
          className="glass-nav fixed bottom-0 left-0 right-0 flex justify-around items-center z-40"
          style={{
            height: 'calc(64px + env(safe-area-inset-bottom, 0px))',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          }}
        >
          {NAV.map(({ to, label, icon }) => (
            <NavLink key={to} to={to} end={to === '/'}
              style={{ flex: 1, display: 'flex', justifyContent: 'center' }}
            >
              {({ isActive }) => (
                <div
                  className={`flex flex-col items-center gap-0.5 px-3 py-2 rounded-2xl transition-all duration-200 ${
                    isActive ? 'nav-pill-active' : ''
                  }`}
                  style={{
                    color: isActive ? '#EEEDFE' : navTheme.muted,
                    minWidth: 52,
                    transform: isActive ? 'scale(1)' : 'scale(0.95)',
                  }}
                >
                  <span style={{ opacity: isActive ? 1 : 0.5 }}>{icon}</span>
                  <span style={{
                    fontSize: 9,
                    fontWeight: isActive ? 700 : 400,
                    fontFamily: 'Syne, sans-serif',
                    letterSpacing: '0.04em',
                    opacity: isActive ? 1 : 0.45,
                  }}>{label}</span>
                </div>
              )}
            </NavLink>
          ))}
        </nav>
      </div>
    </BrowserRouter>
  );
}
